import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { attachment } from "../shared";
import { defineModel } from "../types";

const briaVideoBackgroundRemovalParams = z
  .object({
    attachments: z.array(attachment).optional(),
  })
  .strip();

export const briaVideoBackgroundRemoval = defineModel({
  key: "bria-video-background-removal",
  stepType: "video",
  spec: {
    label: "Remove BG - Premium",
    description: "Remove backgrounds from videos automatically",
    icon: "bg-remove.svg",
    tags: ["video-edit"],
    featured: false,
    info: "Generating a video with this model takes between 5-10 minutes.",
    features: { attachments: true } as const,
    options: {
      attachments: [
        {
          type: "video",
          allowedFormats: ["video/mp4", "video/webm", "video/quicktime"],
          label: "Video",
          slot: "video",
          required: true,
        },
      ],
    },
    params: briaVideoBackgroundRemovalParams,
  },
  referencePricing: {
    provider: "fal.ai",
    url: "https://fal.ai/models/bria-ai/video/background-removal",
    entries: [{ unit: "per_second", unitPriceMicrodollars: 140_000 }],
  },
  providers: [
    {
      provider: "fal",
      modelId: "bria/video/background-removal",
      estimateCost(_params) {
        // fal.ai rate: $0.14 per second of video, estimate 10s default duration
        const DEFAULT_DURATION = 10;
        return 0.14 * DEFAULT_DURATION * 1_000_000 * (1 + BRAND_MARKUP);
      },
      costPerSecond: 0.14 * 1_000_000 * (1 + BRAND_MARKUP),
      toPayload(params) {
        const getUrl = (slot: string) =>
          params.attachments?.find((attachment) => attachment.slot === slot)
            ?.data;

        const videoUrl = getUrl("video");

        if (!videoUrl) {
          throw new Error(
            "Video URL is required for BRIA video background removal",
          );
        }

        return {
          model: this.modelId,
          prompt: "", // BRIA doesn't use prompts, but FalVideoPayload requires it
          video_url: videoUrl,
          background_color: "Transparent", // Always use transparent background
          // Always use webm_vp9 for transparency (VP9 with alpha channel)
          // VP9 provides good compression while supporting transparency, unlike ProRes which is too large
          output_container_and_codec: "webm_vp9",
        };
      },
    },
  ],
});
